import React from 'react'

import { usePanelContext } from '../../contexts'
import { OrderResponseItem, WebEdiDto, WrapperDelFor } from '../../data/models'
import { useConfigContext } from '../../features/ui-config'
import { CreateTooltip } from './CreateTooltip'
import { getConfigKeyByMessageType } from './getConfigKeyByMessageType'
import {
  CheckFieldOptions,
  getMissingFieldsFromData,
} from './getMissingFieldsFromData'
import { getRequiredFieldPathsFromConfig } from './getRequiredFieldPathsFromConfig'

type Props = {
  children: (
    isSendButtonDisabled: boolean,
    tooltip: JSX.Element | null,
  ) => React.ReactNode
  data?: WebEdiDto | WrapperDelFor
  additionalRequiredFieldPaths?: CheckFieldOptions[]
}

export const CheckRequiredFields: React.FC<Props> = ({
  children,
  data,
  additionalRequiredFieldPaths,
}) => {
  const { uiConfig: config } = useConfigContext()
  const { ids } = usePanelContext()

  // if data is not loaded yet disable the send button and set no tooltip
  if (!data) {
    return <>{children(true, null)}</>
  }

  const configKey = getConfigKeyByMessageType(data?.MessageType)

  const requiredFieldPathsFromConfig = getRequiredFieldPathsFromConfig(
    config[configKey],
  )

  let missingFieldsInData = getMissingFieldsFromData(
    requiredFieldPathsFromConfig,
    data,
  )

  if (additionalRequiredFieldPaths) {
    missingFieldsInData = missingFieldsInData
      ? [...missingFieldsInData, ...additionalRequiredFieldPaths]
      : [...additionalRequiredFieldPaths]
  }

  const areAllItemsRejected = (data.Payload.Items as OrderResponseItem[]).every(
    (item: OrderResponseItem) => item.ConfirmationCode === 'REJECTED',
  )

  const isEditModeOn = ids.length > 0

  const isSendButtonDisabled =
    !!missingFieldsInData?.length || areAllItemsRejected || isEditModeOn

  // // If send button is disabled create a tooltip so the user knows what fields need to be filled
  const tooltip = isSendButtonDisabled ? (
    <CreateTooltip
      missingFields={missingFieldsInData}
      areAllItemsRejected={areAllItemsRejected}
      isEditModeOn={isEditModeOn}
      configKey={configKey}
    />
  ) : null

  return <>{children(isSendButtonDisabled, tooltip)}</>
}
